/**
 * Rationing intake client.
 *
 * Routes the production "Save to Life Map" flow through the rationing
 * gate (`POST /api/rationing/v1/intake/accept`). In the shadow phase
 * (`RATIONING_ENFORCE_ACTIVE_LANE=false`) this records what WOULD be
 * parked vs activated and returns a tone-guarded, teaching
 * `summary_copy` — it does not change what gets saved. When the
 * feature flag is off the endpoint returns 503 and every call here is
 * a no-op (returns null), so wiring this in is always safe.
 *
 * Design: docs/AUTO_MOM_FEATURE_2_RATIONING_CONTROLLED_RELEASE.md
 */

export type RationingSource =
  | 'chat_save_modal'
  | 'manual_create'
  | 'import'
  | 'lab';

/** Decisions the classifier can recommend / the repository can apply. */
export type RationingAppliedDecision =
  | 'activate'
  | 'park'
  | 'commit_only'
  | 'legacy_pass_through'
  | 'failed';

/** Item shape the intake endpoint consumes (only fields the gate reads). */
export interface RationingIntakeItemInput {
  node_type: string;
  title?: string;
  description?: string;
  proposed_life_area?: string;
  proposed_priority?: string;
  tentative?: boolean;
  durability_class?: string;
}

export interface RationingDecisionItem {
  intake_item_id: number;
  item_index: number;
  raw_node_type: string;
  node_type: string | null;
  recommended_decision: string;
  applied_decision: RationingAppliedDecision;
  why_code: string;
  why_detail: string;
  applied_why_code: string | null;
  classification_status: string;
  error_message: string | null;
  override_invitation_copy: string | null;
}

export interface RationingAcceptResponse {
  success: boolean;
  batch_id: number;
  idempotency_key: string;
  /** Tone-guarded, saved-not-lost summary. Render this verbatim. */
  summary_copy: string;
  by_decision: Record<string, number>;
  items: RationingDecisionItem[];
}

/**
 * V3 hierarchy item types -> rationing node_type. The rationing
 * classifier only models task / habit / goal / dream (and `action`
 * normalizes to task server-side). Types not in this map are not part
 * of the active-lane model and are left untouched by the gate.
 */
const RATIONING_NODE_TYPE_MAP: Record<string, string> = {
  task: 'task',
  habit: 'habit',
  goal: 'goal',
  dream: 'dream',
  action_item: 'action',
  action: 'action',
};

/** True when this V3 type participates in rationing classification. */
export function isRationableType(v3Type: string | undefined | null): boolean {
  if (!v3Type) return false;
  return v3Type.toLowerCase() in RATIONING_NODE_TYPE_MAP;
}

/**
 * Map one V3 item (already reject-filtered / edit-applied) to an
 * intake row. Returns null for types outside the rationing model.
 */
export function toRationingIntakeItem(item: {
  type?: string;
  title?: string;
  description?: string;
  life_area?: string;
  fields?: Record<string, unknown>;
}): RationingIntakeItemInput | null {
  const v3Type = (item.type || '').toLowerCase();
  const node_type = RATIONING_NODE_TYPE_MAP[v3Type];
  if (!node_type) return null;

  const fields = item.fields ?? {};
  const priority = fields['priority'];
  const durability = fields['durability_class'];
  const tentative = fields['tentative'];

  return {
    node_type,
    title: item.title,
    description: item.description,
    proposed_life_area: item.life_area,
    proposed_priority: typeof priority === 'string' ? priority : undefined,
    tentative: typeof tentative === 'boolean' ? tentative : undefined,
    durability_class:
      typeof durability === 'string' ? durability : undefined,
  };
}

/**
 * Submit a batch to the rationing gate. Non-blocking by contract:
 * returns null on 503 (feature off), any HTTP error, or a network
 * failure, so the caller can always proceed with its normal save.
 */
export async function acceptIntakeBatch(
  items: RationingIntakeItemInput[],
  idempotencyKey: string,
  source: RationingSource = 'chat_save_modal',
  sourceRef?: string,
): Promise<RationingAcceptResponse | null> {
  if (items.length === 0) return null;
  try {
    const resp = await fetch('/api/rationing/v1/intake/accept', {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        idempotency_key: idempotencyKey,
        items,
        source,
        source_ref: sourceRef,
      }),
    });
    if (!resp.ok) {
      // 503 = feature flag off (expected, shadow not active); any other
      // non-2xx is logged but never blocks the save path.
      if (resp.status !== 503) {
        console.warn('[rationing] intake/accept returned', resp.status);
      }
      return null;
    }
    return (await resp.json()) as RationingAcceptResponse;
  } catch (e) {
    console.warn('[rationing] intake/accept failed (non-blocking):', e);
    return null;
  }
}

export interface RationingHierarchyLink {
  intake_item_id: number;
  hierarchy_item_id: number | string;
  hierarchy_item_type: string;
}

/**
 * Record the durable rationing<->hierarchy mapping after the Life Map
 * items are created, so parking-lot activation can flip the linked
 * item's status with no title guessing. Non-blocking: returns the
 * number linked, or 0 on a disabled flag / error.
 */
export async function linkHierarchyItems(
  links: RationingHierarchyLink[],
): Promise<number> {
  if (links.length === 0) return 0;
  try {
    const resp = await fetch('/api/rationing/v1/intake/link', {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ links }),
    });
    if (!resp.ok) {
      if (resp.status !== 503) {
        console.warn('[rationing] intake/link returned', resp.status);
      }
      return 0;
    }
    const body = (await resp.json()) as { linked?: number };
    return body.linked ?? 0;
  } catch (e) {
    console.warn('[rationing] intake/link failed (non-blocking):', e);
    return 0;
  }
}

/** Stable idempotency key for one Save attempt. */
export function newIdempotencyKey(): string {
  try {
    if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) {
      return crypto.randomUUID();
    }
  } catch {
    /* fall through */
  }
  return `rat-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
}
